// FILE: types.ts
// Purpose: Shared web-app view models for threads, projects, terminal layout, and sidebar rows.
// Exports: Runtime UI types consumed across store, routes, and components.

import type {
  ModelSelection,
  OrchestrationMessageSource,
  OrchestrationLatestTurn,
  OrchestrationProposedPlanId,
  OrchestrationSessionStatus,
  OrchestrationThreadActivity,
  ThreadHandoff,
  ProjectScript as ContractProjectScript,
  ProjectLocalAction as ContractProjectLocalAction,
  ProjectRepoCredentialStatus,
  ThreadId,
  ProjectId,
  TurnId,
  MessageId,
  ProviderKind,
  CheckpointRef,
  ProviderInteractionMode,
  RuntimeMode,
  ThreadEnvironmentMode,
  UserId,
  ManagedAgentId,
} from "@taskin-code/contracts";

export type SessionPhase = "disconnected" | "connecting" | "ready" | "running";
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";

export const DEFAULT_INTERACTION_MODE: ProviderInteractionMode = "default";
export const DEFAULT_THREAD_TERMINAL_HEIGHT = 280;
export const DEFAULT_THREAD_TERMINAL_ID = "default";
export const MAX_TERMINALS_PER_GROUP = 6;
export type ThreadTerminalPresentationMode = "drawer" | "workspace";
export type ThreadTerminalWorkspaceTab = "terminal" | "chat";
export type ThreadTerminalWorkspaceLayout = "both" | "terminal-only";
export type ThreadPrimarySurface = "chat" | "terminal";
export type ProjectScript = ContractProjectScript;
export type ProjectLocalAction = ContractProjectLocalAction;

export type ThreadTerminalSplitDirection = "horizontal" | "vertical";
export type ThreadTerminalSplitPosition = "top" | "right" | "bottom" | "left";

export interface ThreadTerminalLeafNode {
  type: "terminal";
  paneId: string;
  terminalIds: string[];
  activeTerminalId: string;
}

export interface ThreadTerminalSplitNode {
  type: "split";
  id: string;
  direction: ThreadTerminalSplitDirection;
  children: ThreadTerminalLayoutNode[];
  weights: number[];
}

export type ThreadTerminalLayoutNode = ThreadTerminalLeafNode | ThreadTerminalSplitNode;

export interface ThreadTerminalGroup {
  id: string;
  activeTerminalId: string;
  layout: ThreadTerminalLayoutNode;
}

export interface ChatImageAttachment {
  type: "image";
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  previewUrl?: string;
}

/**
 * A non-image attachment. Mirrors `ChatFileAttachment` in the contracts.
 *
 * `previewUrl` exists here too, but it addresses the bytes for *download*
 * rather than for rendering: nothing puts one of these in an `<img>`, and the
 * server refuses to serve it inline.
 */
export interface ChatFileAttachment {
  type: "file";
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  previewUrl?: string;
}

export type ChatAttachment = ChatImageAttachment | ChatFileAttachment;

export interface ChatMessage {
  id: MessageId;
  role: "user" | "assistant" | "system";
  text: string;
  attachments?: ChatAttachment[];
  turnId?: TurnId | null;
  createdAt: string;
  completedAt?: string | undefined;
  streaming: boolean;
  source?: OrchestrationMessageSource;
  /**
   * Author of a `user` message, resolved server-side at query time — never
   * synthesize a value client-side. Absent means unknown (no auth, or an
   * old message written before authorship was tracked); render no label.
   */
  authorUserId?: UserId | null;
  authorDisplayName?: string | null;
}

export interface ProposedPlan {
  id: OrchestrationProposedPlanId;
  turnId: TurnId | null;
  planMarkdown: string;
  implementedAt: string | null;
  implementationThreadId: ThreadId | null;
  createdAt: string;
  updatedAt: string;
}

export interface TurnDiffFileChange {
  path: string;
  kind?: string | undefined;
  additions?: number | undefined;
  deletions?: number | undefined;
}

export interface TurnDiffSummary {
  turnId: TurnId;
  completedAt: string;
  status?: string | undefined;
  files: TurnDiffFileChange[];
  checkpointRef?: CheckpointRef | undefined;
  assistantMessageId?: MessageId | undefined;
  checkpointTurnCount?: number | undefined;
}

export interface Project {
  id: ProjectId;
  name: string;
  remoteName: string;
  folderName: string;
  iconFileName: string | null;
  color: string | null;
  description: string | null;
  cwd: string;
  defaultModelSelection: ModelSelection | null;
  expanded: boolean;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  scripts: ProjectScript[];
  /**
   * Commands an agent may run on the *user's own machine* through the desktop
   * app. Project metadata like `scripts`, and empty for every project that has
   * not declared any — including every project stored before the catalog
   * existed, which the contracts schema decodes to `[]`.
   */
  localActions: ProjectLocalAction[];
  /**
   * Projects this project's threads may delegate work into, declared in the
   * project's settings. Empty for every project that declares none.
   */
  linkedProjectIds: ProjectId[];
  agentIds: ManagedAgentId[];
  isGitRepository?: boolean | undefined;
  isBareRepository?: boolean | undefined;
  /**
   * The branch this project's shared environments and pull-request previews are
   * built from. Null until an administrator names one — which is what makes
   * "Nuevo entorno del proyecto" say why it is disabled instead of guessing.
   */
  baseBranch?: string | null | undefined;
  /** GitHub source repo + stored-PAT state, projected by the server. */
  repositoryUrl?: string | null | undefined;
  repositoryDefaultBranch?: string | null | undefined;
  repoCredentialStatus?: ProjectRepoCredentialStatus | undefined;
  repoCredentialError?: string | null | undefined;
  repoCredentialValidatedAt?: string | null | undefined;
  /**
   * Plane board this project tracks its work on, and the workspace it lives in.
   *
   * `planeWorkspaceSlug` is null when the project uses the installation's
   * workspace. No credential state here: an agent acts with the Plane account of
   * whoever owns the thread, so whether Plane is reachable is a property of the
   * person, shown in their own profile, not of the project.
   */
  planeProjectId?: string | null | undefined;
  planeProjectIdentifier?: string | null | undefined;
  planeWorkspaceSlug?: string | null | undefined;
}

export interface ThreadWorkspaceState {
  envMode?: ThreadEnvironmentMode | undefined;
  branch: string | null;
  worktreePath: string | null;
  associatedWorktreePath?: string | null;
  associatedWorktreeBranch?: string | null;
  associatedWorktreeRef?: string | null;
  associatedAgentId?: string | null;
  /**
   * The persistent environment this thread runs in, when its author has one.
   *
   * Server-authoritative and read-only: the client never sends it and cannot
   * change it. Rendered so a shared workspace is visible rather than implied —
   * a PM whose threads all share one worktree should be able to see that they
   * do, since it is why yesterday's test data is still there.
   */
  environmentId?: string | null;
}

export interface ThreadWorkspacePatch {
  envMode?: ThreadEnvironmentMode | undefined;
  branch?: string | null;
  worktreePath?: string | null;
  associatedWorktreePath?: string | null;
  associatedWorktreeBranch?: string | null;
  associatedWorktreeRef?: string | null;
  associatedAgentId?: string | null;
}

export interface Thread extends ThreadWorkspaceState {
  id: ThreadId;
  kind: "standard" | "initiative";
  codexThreadId: string | null;
  projectId: ProjectId;
  title: string;
  modelSelection: ModelSelection;
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;
  session: ThreadSession | null;
  messages: ChatMessage[];
  proposedPlans: ProposedPlan[];
  error: string | null;
  createdAt: string;
  archivedAt?: string | null;
  /**
   * Instant `/clear` cut the conversation view. Anything older is never
   * rendered again and there is deliberately no way back to it.
   */
  contextClearedAt?: string | null;
  updatedAt?: string | undefined;
  latestTurn: OrchestrationLatestTurn | null;
  pendingSourceProposedPlan?: OrchestrationLatestTurn["sourceProposedPlan"];
  /**
   * This reader's own inbox state, never anyone else's: the instant they marked
   * the thread done, and how long they put it aside for. Both null when they
   * did neither. The server keeps one row per (thread, person) and only ever
   * sends the caller their own.
   */
  settledAt?: string | null;
  snoozedUntil?: string | null;
  lastVisitedAt?: string | undefined;
  /**
   * When a snooze of theirs last ran out on its own. The thread is marked as
   * back from a pause while this is newer than `lastVisitedAt`; opening it is
   * what makes the mark go away. Null when no snooze of theirs ever expired.
   */
  wokenAt?: string | null;
  /**
   * One line saying where the thread stands, written server-side when a turn
   * settles in a thread somebody stopped watching. Unlike the four fields
   * above this is not per-person: it is a property of the thread and everyone
   * who can see it sees the same sentence. Absent when the installation has
   * recaps off, or the thread has not earned one yet.
   */
  recap?: { text: string; generatedAt: string } | null;
  parentThreadId?: ThreadId | null;
  subagentAgentId?: string | null;
  subagentNickname?: string | null;
  subagentRole?: string | null;
  forkSourceThreadId?: ThreadId | null;
  handoff?: ThreadHandoff | null;
  /**
   * Plane issue this thread works, when it was opened from one (or linked
   * later). Server-authoritative — the client only renders it.
   */
  planeIssueRef?: string | null;
  planeIssueId?: string | null;
  /**
   * Pull request this thread produced, when it was opened from one (or linked later).
   * Server-authoritative, and only the address: the title and state are read live from GitHub by
   * the Pull Requests section, never copied onto a thread where they would go stale.
   */
  linkedPullRequest?: { projectId: ProjectId; number: number } | null;
  latestUserMessageAt?: string | null;
  hasPendingApprovals?: boolean;
  hasPendingUserInput?: boolean;
  hasActionableProposedPlan?: boolean;
  /** See `SidebarThreadSummary.delegateReportReadAt`. */
  delegateReportReadAt?: string | null;
  /** See `SidebarThreadSummary.delegationWakeSkippedAt`. */
  delegationWakeSkippedAt?: string | null;
  /**
   * Who owns the thread, and who else it is shared with.
   *
   * Server-authoritative and read-only here: the client renders them, but
   * visibility is decided on the server, which simply stops sending threads the
   * user may not see. Undefined on an install without auth.
   */
  createdBy?: UserId | null;
  assignedUserIds?: ReadonlyArray<UserId>;
  turnDiffSummaries: TurnDiffSummary[];
  activities: OrchestrationThreadActivity[];
}

export interface ThreadShell extends ThreadWorkspaceState {
  id: ThreadId;
  kind: "standard" | "initiative";
  codexThreadId: string | null;
  projectId: ProjectId;
  title: string;
  modelSelection: ModelSelection;
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;
  error: string | null;
  createdAt: string;
  archivedAt?: string | null;
  /** See `Thread.contextClearedAt`; a Thread is rebuilt on top of its shell. */
  contextClearedAt?: string | null;
  updatedAt?: string | undefined;
  parentThreadId?: ThreadId | null;
  subagentAgentId?: string | null;
  subagentNickname?: string | null;
  subagentRole?: string | null;
  forkSourceThreadId?: ThreadId | null;
  handoff?: ThreadHandoff | null;
  /**
   * Plane issue this thread works, when it was opened from one (or linked
   * later). Server-authoritative — the client only renders it.
   */
  planeIssueRef?: string | null;
  planeIssueId?: string | null;
  /**
   * Pull request this thread produced, when it was opened from one (or linked later).
   * Server-authoritative, and only the address: the title and state are read live from GitHub by
   * the Pull Requests section, never copied onto a thread where they would go stale.
   */
  linkedPullRequest?: { projectId: ProjectId; number: number } | null;
  latestUserMessageAt?: string | null;
  hasPendingApprovals?: boolean;
  hasPendingUserInput?: boolean;
  hasActionableProposedPlan?: boolean;
  /** See `SidebarThreadSummary.delegateReportReadAt`. */
  delegateReportReadAt?: string | null;
  /** See `SidebarThreadSummary.delegationWakeSkippedAt`. */
  delegationWakeSkippedAt?: string | null;
  /**
   * This reader's own inbox state, never anyone else's: the instant they marked
   * the thread done, and how long they put it aside for. Both null when they
   * did neither. The server keeps one row per (thread, person) and only ever
   * sends the caller their own.
   */
  settledAt?: string | null;
  snoozedUntil?: string | null;
  lastVisitedAt?: string | undefined;
  /**
   * When a snooze of theirs last ran out on its own. The thread is marked as
   * back from a pause while this is newer than `lastVisitedAt`; opening it is
   * what makes the mark go away. Null when no snooze of theirs ever expired.
   */
  wokenAt?: string | null;
  /** See `Thread.recap`: one per thread, the same for everyone who sees it. */
  recap?: { text: string; generatedAt: string } | null;
  /**
   * Who owns the thread, and who else it is shared with.
   *
   * Server-authoritative and read-only here: the client renders them, but
   * visibility is decided on the server, which simply stops sending threads the
   * user may not see. Undefined on an install without auth.
   */
  createdBy?: UserId | null;
  assignedUserIds?: ReadonlyArray<UserId>;
}

export interface ThreadTurnState {
  latestTurn: OrchestrationLatestTurn | null;
  pendingSourceProposedPlan?: OrchestrationLatestTurn["sourceProposedPlan"];
}

export interface SidebarThreadSummary {
  id: ThreadId;
  projectId: ProjectId;
  kind?: "standard" | "initiative";
  title: string;
  modelSelection: ModelSelection;
  interactionMode: ProviderInteractionMode;
  envMode?: ThreadEnvironmentMode | undefined;
  branch: string | null;
  worktreePath: string | null;
  session: ThreadSession | null;
  createdAt: string;
  archivedAt?: string | null;
  updatedAt?: string | undefined;
  latestTurn: OrchestrationLatestTurn | null;
  /**
   * This reader's own inbox state, never anyone else's: the instant they marked
   * the thread done, and how long they put it aside for. Both null when they
   * did neither. The server keeps one row per (thread, person) and only ever
   * sends the caller their own.
   */
  settledAt?: string | null;
  snoozedUntil?: string | null;
  lastVisitedAt?: string | undefined;
  /**
   * When a snooze of theirs last ran out on its own. The thread is marked as
   * back from a pause while this is newer than `lastVisitedAt`; opening it is
   * what makes the mark go away. Null when no snooze of theirs ever expired.
   */
  wokenAt?: string | null;
  /** See `Thread.recap`: one per thread, the same for everyone who sees it. */
  recap?: { text: string; generatedAt: string } | null;
  parentThreadId?: ThreadId | null;
  subagentAgentId?: string | null;
  subagentNickname?: string | null;
  subagentRole?: string | null;
  latestUserMessageAt: string | null;
  hasPendingApprovals: boolean;
  hasPendingUserInput: boolean;
  hasActionableProposedPlan: boolean;
  hasLiveTailWork: boolean;
  /**
   * Read receipt left by the delegating agent's `agent_delegate_poll`. Drives
   * "Delegate Ready" through `hasUnreadDelegateReport`, which is the same
   * predicate the server's wake reactor runs on.
   */
  delegateReportReadAt?: string | null | undefined;
  /**
   * When the server last decided it would not resume this thread on its own
   * while a delegation was owed a read, from the newest
   * `delegation.wake.skipped` activity.
   *
   * This — not "a report is unread" — is what makes a delegating thread the
   * user's business: everything else the wake reactor holds back on resolves
   * itself on the next sweep.
   */
  delegationWakeSkippedAt?: string | null | undefined;
  forkSourceThreadId?: ThreadId | null;
  handoff?: ThreadHandoff | null;
  /** See `Thread.linkedPullRequest`. Carried on the summary so the row can show the mark on
   * first paint, without holding the whole thread. */
  linkedPullRequest?: { projectId: ProjectId; number: number } | null;
}

export interface ThreadSession {
  provider: ProviderKind;
  status: SessionPhase | "error" | "closed";
  activeTurnId?: TurnId | undefined;
  createdAt: string;
  updatedAt: string;
  lastError?: string;
  orchestrationStatus: OrchestrationSessionStatus;
}
